import axios from "axios";
import router from "../router";

const runtimeVariables = (window as any).variables || {};

function isValidApiUrl(value?: string) {
  return !!value && value.indexOf("${") < 0;
}

function resolveApiUrl() {
  if (isValidApiUrl(runtimeVariables.API)) return runtimeVariables.API;
  if (isValidApiUrl(process.env.VUE_APP_API_URL)) return process.env.VUE_APP_API_URL;
  return "https://api-newdental.mazzitech.com.br";
}

axios.defaults.baseURL = resolveApiUrl();

const authClient = axios.create({
  baseURL: axios.defaults.baseURL,
});

const RENEW_THRESHOLD_SECONDS = 120;

type StoredToken = {
  id?: number;
  session?: number;
  access: string;
  permissions?: string[];
};

function getStoredToken(): StoredToken | null {
  try {
    const raw = localStorage.getItem("token");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function decodeJwtPayload(access: string): { exp?: number; [key: string]: any } | null {
  try {
    const payload = access.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const decoded = decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );

    return JSON.parse(decoded);
  } catch (e) {
    return null;
  }
}

function getSessionId(token: StoredToken | null) {
  return token && (token.session || token.id);
}

function isSessionEndpoint(url?: string) {
  return !!url && url.indexOf("/sessions") >= 0;
}

function shouldRenewToken(token: StoredToken | null) {
  if (!token || !token.access) return false;
  const payload = decodeJwtPayload(token.access);
  if (!payload || !payload.exp) return false;

  const secondsUntilExpiration = payload.exp - Math.floor(Date.now() / 1000);
  return secondsUntilExpiration <= RENEW_THRESHOLD_SECONDS;
}

function persistToken(data: StoredToken, fallbackSessionId?: number) {
  const token = {
    ...data,
    session: data.session || data.id || fallbackSessionId,
  };

  localStorage.setItem("token", JSON.stringify(token));
  axios.defaults.headers.common["Authorization"] = "Bearer " + token.access;

  const rawUser = localStorage.getItem("user");
  if (rawUser) {
    try {
      const user = JSON.parse(rawUser);
      localStorage.setItem("user", JSON.stringify({
        ...user,
        permissions: token.permissions || user.permissions || [],
      }));
    } catch (e) {
      // Ignore malformed cached user data. The token is still valid.
    }
  }

  return token;
}

function logout() {
  localStorage.clear();
  if (router.currentRoute.path !== "/login") {
    router.push("/login");
  }
}

let isRefreshing = false;
let refreshPromise: Promise<StoredToken> | null = null;
let pendingQueue: Array<{ resolve: (t: string) => void; reject: (e: any) => void }> = [];

function processQueue(error: any, access: string | null) {
  pendingQueue.forEach(function(p) {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(access as string);
    }
  });
  pendingQueue = [];
}

async function renewToken() {
  const token = getStoredToken();
  const sessionId = getSessionId(token);

  if (!token || !sessionId) {
    throw new Error("session not available");
  }

  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = authClient
    .get("/sessions/" + sessionId + "/access/new", {
      params: { access: token.access },
    })
    .then(function(res) {
      const newToken = persistToken(res.data.data, Number(sessionId));
      processQueue(null, newToken.access);
      return newToken;
    })
    .catch(function(error) {
      processQueue(error, null);
      throw error;
    })
    .finally(function() {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
}

axios.interceptors.request.use(
  async function(config) {
    config.headers["Content-Type"] = "application/json";

    let token = getStoredToken();
    if (!isSessionEndpoint(config.url) && shouldRenewToken(token)) {
      try {
        token = await renewToken();
      } catch (error) {
        logout();
        return Promise.reject(error);
      }
    }

    if (token && token.access) {
      config.headers["Authorization"] = "Bearer " + token.access;
    }
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function(response) {
    return response;
  },
  async function(error) {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.request && error.request.status === 0) {
      return Promise.reject(error);
    }

    const status = error.response && error.response.status;
    const authExpired = status === 401;

    if (authExpired && !originalRequest._retry) {
      if (isSessionEndpoint(originalRequest.url)) {
        logout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          pendingQueue.push({
            resolve: function(access) {
              originalRequest.headers["Authorization"] = "Bearer " + access;
              resolve(axios(originalRequest));
            },
            reject: reject,
          });
        });
      }

      originalRequest._retry = true;

      try {
        const newToken = await renewToken();
        originalRequest.headers["Authorization"] = "Bearer " + newToken.access;
        return axios(originalRequest);
      } catch (refreshError) {
        logout();
        return Promise.reject(refreshError);
      }
    }

    if (error.response && error.response.data && "success" in error.response.data) {
      return Promise.reject(error.response.data);
    }

    return Promise.reject(error);
  }
);
